// Daily entry table - PC view with 3 parallel tracks and half-hour splits
// Cell sizing: 1-hour = H×H square, 30-min = 2H×H wide rectangle.

import React from 'react';
import { CATEGORIES, CAT_BY_ID, CAT_TREE } from '../data/categories.js';
import { SLOTS, SLOT_COUNT } from '../data/slots.js';

const TRACK_LABELS = ['娱乐·吃', '睡眠', '运动'];
// 轨道家族色（不是某条 cat 的色）：用于详情卡片里"X 轴"文字着色，让轨道身份一眼可辨
const TRACK_FAMILY_COLOR = ['var(--c-fun)', 'var(--c-sleep)', 'var(--c-exer)'];

// Recommended schedule bands shown between the time-segment row and hour-number row.
// Bands map to image columns 1-24, which correspond to our internal hours 0-23
// (image col N = hour N-1). Tone: 'sleep' = purple, 'sleep-dim' = lighter purple (午睡), 'focus' = green.
const RECOMMEND_BANDS = [
  { from: 0,  to: 5,  label: '预计睡眠', tone: 'sleep' },
  { from: 6,  to: 6,  label: '起床',     tone: 'focus' },
  { from: 7,  to: 8,  label: '早餐运动', tone: 'focus' },
  { from: 9,  to: 11, label: '高效记忆', tone: 'focus' },
  { from: 12, to: 12, label: '午餐',     tone: 'focus' },
  { from: 13, to: 14, label: '午睡',     tone: 'sleep-dim' },
  { from: 15, to: 17, label: '计算',     tone: 'focus' },
  { from: 18, to: 19, label: '晚餐',     tone: 'focus' },
  { from: 20, to: 21, label: '瑜伽',     tone: 'focus' },
  { from: 22, to: 22, label: '准备睡觉', tone: 'focus' },
  { from: 23, to: 23, label: '睡觉',     tone: 'sleep' },
];
const HOURS_FOR_HEADER = (() => {
  const map = {};
  SLOTS.forEach((s, i) => {
    if (!map[s.h]) map[s.h] = [];
    map[s.h].push(i);
  });
  return Object.keys(map).map(h => ({ h: Number(h), idxs: map[h] }));
})();

const TIME_SEGMENTS = [
  { label: '凌晨', range: [0, 5]   },
  { label: '早晨', range: [6, 9]   },
  { label: '上午', range: [10, 12] },
  { label: '中午', range: [13, 15] },
  { label: '下午', range: [16, 18] },
  { label: '晚上', range: [19, 21] },
  { label: '夜间', range: [22, 23] },
];

function formatTimeRange(slotIdxs) {
  if (!slotIdxs || slotIdxs.length === 0) return '';
  slotIdxs = [...slotIdxs].sort((a,b) => a-b);
  // group into contiguous ranges
  const ranges = [];
  let start = slotIdxs[0], prev = slotIdxs[0];
  for (let i = 1; i < slotIdxs.length; i++) {
    if (slotIdxs[i] === prev + 1) prev = slotIdxs[i];
    else { ranges.push([start, prev]); start = prev = slotIdxs[i]; }
  }
  ranges.push([start, prev]);
  return ranges.map(([a, b]) => {
    const s = SLOTS[a];
    const e = SLOTS[b];
    const endH = e.h, endM = e.m + (e.span === 0.5 ? 30 : 60);
    const eh = endM >= 60 ? endH + 1 : endH;
    const em = endM >= 60 ? endM - 60 : endM;
    return `${pad(s.h)}:${pad(s.m)} – ${pad(eh)}:${pad(em)}`;
  }).join(' / ');
}
function pad(n) { return String(n).padStart(2, '0'); }

// Format prev-tail arrIdx selection (0/1/2 → 21:00/22:00/23:00) as time ranges
function formatPrevTailRange(arrs, prevSlotIdxs) {
  if (!arrs || arrs.length === 0) return '';
  const sorted = [...arrs].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0], prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) prev = sorted[i];
    else { ranges.push([start, prev]); start = prev = sorted[i]; }
  }
  ranges.push([start, prev]);
  // arrIdx → 真实 slot 起止时间（支持 22/23 半点）
  const startLabel = (a) => { const s = SLOTS[prevSlotIdxs[a]]; return s ? `${pad(s.h)}:${pad(s.m)}` : ''; };
  const endLabel = (b) => {
    const s = SLOTS[prevSlotIdxs[b]];
    if (!s) return '';
    const endM = s.m + (s.span === 0.5 ? 30 : 60);
    const eh = endM >= 60 ? s.h + 1 : s.h;
    const em = endM >= 60 ? endM - 60 : endM;
    return `${pad(eh)}:${pad(em)}`;
  };
  return ranges.map(([a, b]) => `${startLabel(a)} – ${endLabel(b)}`).join(' / ');
}

function EditableRange({ startValue, endValue, startOpts, endOpts, onChange }) {
  // value semantics: arrIdx in unified coord (caller pre-builds the option arrays,
  // typically ±6h around the current value to keep the picker focused on 微调).
  return (
    <span className="dt-range-edit">
      <select
        className="dt-time-sel"
        value={startValue}
        onChange={e => {
          const v = +e.target.value;
          onChange(v, v > endValue ? v : endValue);
        }}
      >
        {startOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className="dt-range-dash">–</span>
      <select
        className="dt-time-sel"
        value={endValue}
        onChange={e => {
          const v = +e.target.value;
          onChange(v < startValue ? v : startValue, v);
        }}
      >
        {endOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </span>
  );
}

function DailyTable({ day, onCellSet, onApplySelection, onMoveBlock, onMoveBlockCrossDay, onMigratePomoTrack, onDetachPomos, onValidateBlockMove, onPasteBlock, selectedCat, onSelectedCat, onNote, onNotes, density = 'comfortable', prevDay, prevDateKey, onPrevCellSet, onPrevApplySelection, onPrevNotes, canUndo, onUndo, quickApplyIds, onQuickApplyChange, quickApplyCollapsed, onQuickApplyCollapsedChange, defaultQuickApplyIds, customCats, catOrder, locked = false }) {
  const [selection, setSelection] = React.useState(new Set());
  const [dragging, setDragging] = React.useState(false);
  const [dragMode, setDragMode] = React.useState('add');
  const [anchor, setAnchor] = React.useState(null); // {t, i} — last single-clicked cell, for shift+click range
  const [moveDrag, setMoveDrag] = React.useState(null); // {srcTrack, srcStart, srcEnd, srcCat, anchorOffset, hoverTrack, hoverStart, valid}
  const pressRef = React.useRef(null); // {t, i, range, cat, x, y, startedSelection}
  const moveDragRef = React.useRef(null);
  const tableRef = React.useRef(null);      // 网格容器，挂原生触摸监听
  const touchDragRef = React.useRef(false);  // 触摸拖选进行中（同步标记，避开 dragging state 异步竞态）
  const [clipboard, setClipboard] = React.useState(null);
  const [expandPrev, setExpandPrev] = React.useState(false);
  const [locatedKeys, setLocatedKeys] = React.useState(new Set());
  const locateTimerRef = React.useRef(null);

  // Map prev tail position 0/1/2 -> real slot idx in SLOTS for hours 21/22/23
  const PREV_TAIL_HOURS = [21, 22, 23];
  // 昨晚尾：21/22/23 的全部 slot（22/23 半点后 = 5 格：21:00 / 22:00 / 22:30 / 23:00 / 23:30）。
  // PREV_LEN 取代原先写死的 3，全坐标系按它动态。
  const prevSlotIdxs = React.useMemo(() => SLOTS.map((s, i) => ({ s, i })).filter(o => PREV_TAIL_HOURS.includes(o.s.h)).map(o => o.i), []);
  const PREV_LEN = prevSlotIdxs.length;
  function prevKey(t, p) { return `p:${t}:${p}`; }

  // Auto-expand 昨 when today's earliest cells (slot 0) continue from yesterday with same cat
  // — i.e., the day starts mid-block. Lets user see/edit the cross-midnight block as one unit
  // without having to manually toggle the panel.
  React.useEffect(() => {
    if (expandPrev) return;
    if (!prevDay || !prevDay.tracks || !day || !day.tracks) return;
    const slot23 = prevSlotIdxs[PREV_LEN - 1];
    if (slot23 == null || slot23 < 0) return;
    for (const t of [0, 1, 2]) {
      const tc = day.tracks[t] && day.tracks[t][0];
      const pc = prevDay.tracks[t] && prevDay.tracks[t][slot23];
      if (tc && tc === pc) {
        setExpandPrev(true);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevDateKey, day && day.date]);

  // Cell sizing — height = unit; 1-hour width = unit (1:1); 30-min width = 2*unit (2:1)
  const unit = density === 'compact' ? 22 : 28;
  const hourW = unit;
  const halfW = unit * 2;
  function slotW(s) { return s.span === 0.5 ? halfW : hourW; }
  // 昨晚尾真实总宽 / 每小时宽（22/23 是 2 个半格 → 比整点宽，不能写死 unit）
  const prevTailWidth = prevSlotIdxs.reduce((a, i) => a + slotW(SLOTS[i]), 0);
  const prevHourW = (h) => SLOTS.filter(s => s.h === h).reduce((a, s) => a + slotW(s), 0);

  // Unified coordinate system: when expandPrev is true, arrIdx 0..2 = prev 21/22/23,
  // arrIdx 3..(3+SLOT_COUNT-1) = today 0..SLOT_COUNT-1. When not expanded, arrIdx = today slot idx.
  const unifiedLen = expandPrev ? PREV_LEN + SLOT_COUNT : SLOT_COUNT;
  function arrIdxIsPrev(arrIdx) { return expandPrev && arrIdx < PREV_LEN; }
  function arrIdxToTodayIdx(arrIdx) { return expandPrev ? arrIdx - PREV_LEN : arrIdx; }
  function todayIdxToArrIdx(i) { return expandPrev ? PREV_LEN + i : i; }
  function getCellCat(t, arrIdx) {
    if (arrIdxIsPrev(arrIdx)) {
      const psIdx = prevSlotIdxs[arrIdx];
      return (prevDay && prevDay.tracks && prevDay.tracks[t] && prevDay.tracks[t][psIdx]) || null;
    }
    const i = arrIdxToTodayIdx(arrIdx);
    return day.tracks[t][i];
  }
  function getCellWidth(arrIdx) {
    if (arrIdxIsPrev(arrIdx)) return unit;
    return slotW(SLOTS[arrIdxToTodayIdx(arrIdx)]);
  }
  function getCellNote(t, arrIdx) {
    if (arrIdxIsPrev(arrIdx)) {
      const psIdx = prevSlotIdxs[arrIdx];
      return prevDay && prevDay.notes && prevDay.notes[`${t}:${psIdx}`];
    }
    return day.notes[`${t}:${arrIdxToTodayIdx(arrIdx)}`];
  }
  function getCellAlign(t, arrIdx) {
    if (arrIdxIsPrev(arrIdx)) {
      const psIdx = prevSlotIdxs[arrIdx];
      return (prevDay && prevDay.noteAligns && prevDay.noteAligns[`${t}:${psIdx}`]) || 'left';
    }
    return (day.noteAligns && day.noteAligns[`${t}:${arrIdxToTodayIdx(arrIdx)}`]) || 'left';
  }
  function getCellTruncate(t, arrIdx) {
    if (arrIdxIsPrev(arrIdx)) {
      const psIdx = prevSlotIdxs[arrIdx];
      return (prevDay && prevDay.noteTruncates && prevDay.noteTruncates[`${t}:${psIdx}`]) || 'ellipsis';
    }
    return (day.noteTruncates && day.noteTruncates[`${t}:${arrIdxToTodayIdx(arrIdx)}`]) || 'ellipsis';
  }
  function arrIdxToKey(t, arrIdx) {
    if (arrIdxIsPrev(arrIdx)) return prevKey(t, arrIdx);
    return cellKey(t, arrIdxToTodayIdx(arrIdx));
  }

  // Auto-derive merges on the UNIFIED scale: any run of >=2 contiguous same-category cells.
  // start/end are arrIdx-based; merges may span the prev↔today boundary (arrIdx 2 ↔ 3).
  const merges = React.useMemo(() => {
    const out = [];
    for (let t = 0; t < 3; t++) {
      let start = 0, cur = getCellCat(t, 0);
      for (let arrIdx = 1; arrIdx <= unifiedLen; arrIdx++) {
        const v = arrIdx < unifiedLen ? getCellCat(t, arrIdx) : null;
        if (v !== cur) {
          if (cur != null && arrIdx - 1 > start) out.push({ track: t, start, end: arrIdx - 1, catId: cur });
          start = arrIdx; cur = v;
        }
      }
    }
    return out;
  }, [day.tracks, expandPrev, prevDay && prevDay.tracks]);
  function mergeFor(t, arrIdx) { return merges.find(m => m.track === t && arrIdx >= m.start && arrIdx <= m.end); }

  // Map "<track>:<slotIdx>" → applied pomo log (for cell tooltip enrichment)
  const pomoCellMap = React.useMemo(() => {
    const out = {};
    const logs = (day.pomoLogs || []).filter(l => l.applied && l.appliedTrack != null);
    if (logs.length === 0) return out;
    logs.forEach(l => {
      const sd = new Date(l.startMs);
      const ed = new Date(l.endMs);
      const startMin = sd.getHours() * 60 + sd.getMinutes() + sd.getSeconds() / 60;
      const endMin = ed.getHours() * 60 + ed.getMinutes() + ed.getSeconds() / 60;
      SLOTS.forEach((s, i) => {
        const ss = s.h * 60 + s.m;
        const se = ss + s.span * 60;
        if (se > startMin && ss < endMin) out[`${l.appliedTrack}:${i}`] = l;
      });
    });
    return out;
  }, [day.pomoLogs]);
  function formatPomoRange(l) {
    const fmt = ms => {
      const d = new Date(ms);
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
    };
    const m = l.durationMin;
    const dur = (m >= 60 && m % 60 === 0) ? `${m/60}h` : `${m}m`;
    return `${fmt(l.startMs)}-${fmt(l.endMs)} ${dur}`;
  }

  // Get the category at any arrIdx (works across prev tail + today)
  function catAtArr(t, arr) {
    if (arr < PREV_LEN && expandPrev) {
      const psIdx = prevSlotIdxs[arr];
      if (psIdx == null || psIdx < 0) return null;
      return (prevDay && prevDay.tracks && prevDay.tracks[t] && prevDay.tracks[t][psIdx]) || null;
    }
    const i = expandPrev ? arr - PREV_LEN : arr;
    return (day.tracks[t] && day.tracks[t][i]) || null;
  }
  // Walk left/right from arrIdx on track t, gathering contiguous same-cat cells (visual block).
  // Replaces relying solely on day.merges (which is only populated when user applied via drag-select).
  function contiguousRangeAt(t, arr, cat) {
    let left = arr, right = arr;
    while (left > 0 && catAtArr(t, left - 1) === cat) left--;
    while (right < unifiedLen - 1 && catAtArr(t, right + 1) === cat) right++;
    return { start: left, end: right };
  }

  React.useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      // 1-7: 原内置 cat.key 匹配
      let cat = CATEGORIES.find(c => c.key === e.key);
      // 8-9: 动态绑 quickApplyIds 第 8 / 9 项（位置 7 / 8）
      if (!cat && (e.key === '8' || e.key === '9') && quickApplyIds) {
        const idx = e.key === '8' ? 7 : 8;
        const id = quickApplyIds[idx];
        if (id) cat = CAT_BY_ID[id];
      }
      if (cat) { onSelectedCat(cat.id); applyToSelection(cat.id); }
      if (e.key === 'Escape') setSelection(new Set());
      if (e.key === 'Delete' || e.key === 'Backspace') applyToSelection(null);
    }
    // 全面板（最全的时间分类）点击应用：有选区时弹确认，防误点覆盖（本想切番茄分类却忘了清选区）。
    // 仅拦此路径；快速应用按钮 + 数字键 1-7 走 applyToSelection 不经过这里，照常不弹。
    function onApplyCat(e) {
      const catId = e.detail.catId;
      if (selection.size > 0) {
        const c = CAT_BY_ID[catId] || (customCats || []).find(x => x.id === catId);
        const name = c ? (c.name || c.label) : catId;
        if (!window.confirm(`把选中的 ${selection.size} 格应用为「${name}」？\n会覆盖这些时间块的现有分类（可撤销）。`)) return;
      }
      applyToSelection(catId);
    }
    function onClearSel() { setSelection(new Set()); }
    window.addEventListener('keydown', onKey);
    window.addEventListener('applyCat', onApplyCat);
    window.addEventListener('clearCellSelection', onClearSel);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('applyCat', onApplyCat); window.removeEventListener('clearCellSelection', onClearSel); };
  });

  function applyToSelection(catId) {
    if (selection.size === 0) return;
    const todayEntries = [];
    const prevEntries = [];
    selection.forEach(k => {
      if (k.startsWith('p:')) {
        const parts = k.split(':');
        const t = +parts[1], p = +parts[2];
        prevEntries.push({ track: t, idx: prevSlotIdxs[p] });
      } else {
        const [t, i] = k.split(':').map(Number);
        todayEntries.push({ track: t, idx: i });
      }
    });
    if (catId == null) {
      if (todayEntries.length && onCellSet) onCellSet(todayEntries.map(e => ({ ...e, cat: null })));
      if (prevEntries.length && onPrevCellSet) onPrevCellSet(prevEntries.map(e => ({ ...e, cat: null })));
    } else {
      if (todayEntries.length && onApplySelection) onApplySelection(catId, todayEntries);
      if (prevEntries.length && onPrevApplySelection) onPrevApplySelection(catId, prevEntries);
    }
  }
  function clearSelectedCells() {
    if (selection.size === 0) return;
    applyToSelection(null);
    // 同时把这些格子上的番茄解绑回「未入轴」池，否则 cat 已清空但番茄还 applied，格子删不干净
    if (onDetachPomos) {
      const byToday = {}, byPrev = {};
      selection.forEach(k => {
        if (k.startsWith('p:')) {
          const parts = k.split(':');
          const t = +parts[1], p = +parts[2];
          (byPrev[t] = byPrev[t] || []).push(prevSlotIdxs[p]);
        } else {
          const [t, i] = k.split(':').map(Number);
          (byToday[t] = byToday[t] || []).push(i);
        }
      });
      const tracks = new Set([...Object.keys(byToday), ...Object.keys(byPrev)].map(Number));
      tracks.forEach(t => onDetachPomos(t, byToday[t] || [], byPrev[t] || []));
    }
  }

  function cellKey(t, i) { return `${t}:${i}`; }
  function isSelected(t, i) { return selection.has(cellKey(t, i)); }

  function expandMergeIntoKeys(t, i) {
    const arrIdx = todayIdxToArrIdx(i);
    return expandMergeIntoKeysByArrIdx(t, arrIdx);
  }
  function expandMergeIntoKeysByArrIdx(t, arrIdx) {
    const m = mergeFor(t, arrIdx);
    if (!m) return [arrIdxToKey(t, arrIdx)];
    const out = [];
    for (let a = m.start; a <= m.end; a++) out.push(arrIdxToKey(t, a));
    return out;
  }

  function onPrevCellDown(t, p, e) {
    if (locked) return; // 三轴锁定：不编辑（让原生滑动接管）。也挡掉触摸 tap 合成的鼠标事件
    e.preventDefault();
    if (e.shiftKey && anchor && anchor.t === t) {
      // Range select across boundary from anchor to current prev cell p
      const anchorArr = anchor.kind === 'p' ? anchor.i : todayIdxToArrIdx(anchor.i);
      const curArr = p;
      const a = Math.min(anchorArr, curArr), b = Math.max(anchorArr, curArr);
      const range = new Set();
      for (let aIdx = a; aIdx <= b; aIdx++) expandMergeIntoKeysByArrIdx(t, aIdx).forEach(k => range.add(k));
      setSelection(range);
      setDragMode('add');
      setDragging(true);
      return;
    }
    const k = prevKey(t, p);
    const keys = expandMergeIntoKeysByArrIdx(t, p);
    if (e.shiftKey) {
      const next = new Set(selection);
      const allOn = keys.every(kk => next.has(kk));
      if (allOn) keys.forEach(kk => next.delete(kk));
      else keys.forEach(kk => next.add(kk));
      setSelection(next);
      setDragMode(allOn ? 'remove' : 'add');
    } else {
      setSelection(new Set(keys));
      setDragMode('add');
      setAnchor({ kind: 'p', t, i: p });
      // If this prev cell has a category, record potential move-drag with visual-contiguous range
      // (range can extend into today if today's slot 0+ has same cat — cross-midnight block)
      const psIdx = prevSlotIdxs[p];
      const cat = (prevDay && prevDay.tracks && prevDay.tracks[t] && prevDay.tracks[t][psIdx]) || null;
      if (cat) {
        const range = contiguousRangeAt(t, p, cat);
        pressRef.current = { t, arrIdx: p, range, cat, x: e.clientX, y: e.clientY };
      }
    }
    setDragging(true);
  }
  function onPrevCellEnter(t, p) {
    // Move-drag hover into prev tail — update target in unified arrIdx coord
    if (moveDragRef.current) {
      const md = moveDragRef.current;
      const len = md.srcEnd - md.srcStart + 1;
      let target = p - md.anchorOffset;
      if (target < 0) target = 0;
      if (target + len > unifiedLen) target = unifiedLen - len;
      const next = { ...md, hoverTrack: t, hoverArrIdx: target, hoverCellArr: p };
      moveDragRef.current = next;
      setMoveDrag(next);
      return;
    }
    if (!dragging) return;
    // Promote selection drag to move drag if source was a prev cell with a category
    if (pressRef.current && (pressRef.current.t !== t || pressRef.current.arrIdx !== p)) {
      const pr = pressRef.current;
      pressRef.current = null;
      const anchorOffset = pr.arrIdx - pr.range.start;
      const len = pr.range.end - pr.range.start + 1;
      const md = {
        srcTrack: pr.t,
        srcStart: pr.range.start,
        srcEnd: pr.range.end,
        srcCat: pr.cat,
        anchorOffset,
        hoverTrack: t,
        hoverArrIdx: Math.max(0, Math.min(unifiedLen - len, p - anchorOffset)),
        hoverCellArr: p,
      };
      moveDragRef.current = md;
      setMoveDrag(md);
      return;
    }
    const keys = expandMergeIntoKeysByArrIdx(t, p);
    const next = new Set(selection);
    if (dragMode === 'add') keys.forEach(k => next.add(k));
    else keys.forEach(k => next.delete(k));
    setSelection(next);
  }

  function onCellDown(t, i, e) {
    if (locked) return; // 三轴锁定：不编辑（让原生滑动接管）。也挡掉触摸 tap 合成的鼠标事件
    e.preventDefault();
    const keys = expandMergeIntoKeys(t, i);
    if (e.shiftKey && anchor && anchor.t === t) {
      // Range select within the same track from anchor to current — supports cross-boundary
      const anchorArr = anchor.kind === 'p' ? anchor.i : todayIdxToArrIdx(anchor.i);
      const curArr = todayIdxToArrIdx(i);
      const a = Math.min(anchorArr, curArr), b = Math.max(anchorArr, curArr);
      const range = new Set();
      for (let aIdx = a; aIdx <= b; aIdx++) expandMergeIntoKeysByArrIdx(t, aIdx).forEach(k => range.add(k));
      setSelection(range);
      setDragMode('add');
      setDragging(true);
      return;
    }
    if (e.shiftKey) {
      const next = new Set(selection);
      const allOn = keys.every(k => next.has(k));
      if (allOn) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      setSelection(next);
      setDragMode(allOn ? 'remove' : 'add');
    } else {
      setSelection(new Set(keys));
      setDragMode('add');
      setAnchor({ kind: 't', t, i });
      // If this cell has a category, record potential move-drag (arrIdx + visual-contiguous range)
      const cat = day.tracks[t][i];
      if (cat) {
        const arrIdx = todayIdxToArrIdx(i);
        const range = contiguousRangeAt(t, arrIdx, cat);
        pressRef.current = { t, arrIdx, range, cat, x: e.clientX, y: e.clientY };
      }
    }
    setDragging(true);
  }
  function onCellEnter(t, i) {
    // If a move-drag is in progress, update hover target (unified arrIdx coord)
    if (moveDragRef.current) {
      const md = moveDragRef.current;
      const len = md.srcEnd - md.srcStart + 1;
      const curArr = todayIdxToArrIdx(i);
      let target = curArr - md.anchorOffset;
      if (target < 0) target = 0;
      if (target + len > unifiedLen) target = unifiedLen - len;
      const next = { ...md, hoverTrack: t, hoverArrIdx: target, hoverCellArr: curArr };
      moveDragRef.current = next;
      setMoveDrag(next);
      return;
    }
    if (!dragging) return;
    // Promote selection drag to move drag if user moves a colored block into a new cell.
    // All coords in unified arrIdx.
    const curArr = todayIdxToArrIdx(i);
    if (pressRef.current && (pressRef.current.t !== t || pressRef.current.arrIdx !== curArr)) {
      const pr = pressRef.current;
      pressRef.current = null;
      const anchorOffset = pr.arrIdx - pr.range.start;
      const len = pr.range.end - pr.range.start + 1;
      const md = {
        srcTrack: pr.t,
        srcStart: pr.range.start,
        srcEnd: pr.range.end,
        srcCat: pr.cat,
        anchorOffset,
        hoverTrack: t,
        hoverArrIdx: Math.max(0, Math.min(unifiedLen - len, curArr - anchorOffset)),
        hoverCellArr: curArr,
      };
      moveDragRef.current = md;
      setMoveDrag(md);
      return;
    }
    const keys = expandMergeIntoKeys(t, i);
    const next = new Set(selection);
    if (dragMode === 'add') keys.forEach(k => next.add(k));
    else keys.forEach(k => next.delete(k));
    setSelection(next);
  }
  React.useEffect(() => {
    function up() {
      setDragging(false);
      pressRef.current = null;
      touchDragRef.current = false;
      // Commit move-drag if active (all coordinates in arrIdx)
      const md = moveDragRef.current;
      if (md && md.hoverTrack != null && md.hoverArrIdx != null) {
        const len = md.srcEnd - md.srcStart + 1;
        const srcArrStart = md.srcStart;
        const dstArrStart = md.hoverArrIdx;
        // No snap-to-side: anchor-based positioning lets user freely land the block at any
        // arrIdx, including cross-boundary positions like 22-1 / 23-2 (user feedback B9).
        // Visual drag-tgt highlight on cells already shows the target position live.
        const sameSpot = md.hoverTrack === md.srcTrack && dstArrStart === srcArrStart;
        if (!sameSpot) {
          // Build src and dst cell lists in unified form
          const arrToCell = (arr) =>
            (arr < PREV_LEN && expandPrev)
              ? { isPrev: true, idx: prevSlotIdxs[arr] }
              : { isPrev: false, idx: arrIdxToTodayIdx(arr) };
          const srcCells = [];
          const dstCells = [];
          for (let k = 0; k < len; k++) {
            srcCells.push(arrToCell(srcArrStart + k));
            dstCells.push(arrToCell(dstArrStart + k));
          }
          const allTodaySrc = srcCells.every(c => !c.isPrev);
          const allTodayDst = dstCells.every(c => !c.isPrev);
          if (allTodaySrc && allTodayDst && md.srcTrack === md.hoverTrack && onMoveBlock) {
            // Pure same-day, same-track — use existing moveBlock (preserves swap-detection logic)
            onMoveBlock(md.srcTrack, srcCells[0].idx, srcCells[len - 1].idx, md.hoverTrack, dstCells[0].idx);
          } else if (onMoveBlockCrossDay) {
            onMoveBlockCrossDay({ srcTrack: md.srcTrack, srcCells, dstTrack: md.hoverTrack, dstCells, cat: md.srcCat });
          }
          // Re-select destination cells (arrIdx keyed)
          const next = new Set();
          for (let k = 0; k < len; k++) {
            const arr = dstArrStart + k;
            if (arr < PREV_LEN && expandPrev) next.add(prevKey(md.hoverTrack, arr));
            else next.add(`${md.hoverTrack}:${arrIdxToTodayIdx(arr)}`);
          }
          setSelection(next);
          if (dstArrStart < PREV_LEN && expandPrev) setAnchor({ kind: 'p', t: md.hoverTrack, i: dstArrStart });
          else setAnchor({ kind: 't', t: md.hoverTrack, i: arrIdxToTodayIdx(dstArrStart) });
        }
      }
      moveDragRef.current = null;
      setMoveDrag(null);
    }
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    window.addEventListener('touchcancel', up);
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchcancel', up);
    };
  });

  // 移动端触摸拖选：React 的 onTouchMove 是 passive，preventDefault 不生效 → 用原生非 passive 监听。
  // 触摸划过沿途格子不触发 mouseenter → 用 elementFromPoint 找手指下方的格子，复用鼠标那套 enter 逻辑。
  // 无依赖数组：每次渲染重绑，确保 onCellDown/onCellEnter 等闭包永远读最新 state（与上方 mouseup 同款模式）。
  React.useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    function resolveCell(node) {
      const cell = node && node.closest && node.closest('.cell');
      if (!cell) return null;
      const key = cell.getAttribute('data-cell-key');
      if (!key) return null;
      if (key.startsWith('p:')) { const [, t, p] = key.split(':'); return { kind: 'p', t: +t, p: +p }; }
      const [t, i] = key.split(':'); return { kind: 't', t: +t, i: +i };
    }
    function onStart(e) {
      if (locked) return; // 三轴锁定：放行原生滑动（竖滑翻页 / 横滑平移三轴），不进拖选
      if (e.touches.length !== 1) return; // 多指手势（缩放等）不接管
      const info = resolveCell(e.target);
      if (!info) return;
      const tc = e.touches[0];
      const syn = { preventDefault() {}, shiftKey: false, clientX: tc.clientX, clientY: tc.clientY };
      touchDragRef.current = true;
      if (info.kind === 't') onCellDown(info.t, info.i, syn);
      else onPrevCellDown(info.t, info.p, syn);
      e.preventDefault();
    }
    function onMove(e) {
      if (!touchDragRef.current) return;
      const tc = e.touches[0];
      if (!tc) return;
      const info = resolveCell(document.elementFromPoint(tc.clientX, tc.clientY));
      if (info) {
        if (info.kind === 't') onCellEnter(info.t, info.i);
        else onPrevCellEnter(info.t, info.p);
      }
      e.preventDefault(); // 非 passive，阻止滚动 / 长按菜单
    }
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  });

  // Ctrl+C / Ctrl+V (and ⌘C / ⌘V on Mac) — copy & paste selected block(s) within the day
  React.useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === 'c' || e.key === 'C') {
        if (selection.size === 0) return;
        // Group selection by track; pick first track + its contiguous range (skip prev-tail keys)
        const byTrack = { 0: [], 1: [], 2: [] };
        selection.forEach(k => {
          if (k.startsWith('p:')) return;
          const [tt, ii] = k.split(':').map(Number);
          byTrack[tt].push(ii);
        });
        for (const t of [0, 1, 2]) {
          if (byTrack[t].length === 0) continue;
          const sorted = byTrack[t].sort((a, b) => a - b);
          const start = sorted[0], end = sorted[sorted.length - 1];
          const cells = [], notes = [], aligns = [];
          for (let k = start; k <= end; k++) {
            cells.push(day.tracks[t][k]);
            notes.push(day.notes[`${t}:${k}`] || null);
            aligns.push((day.noteAligns && day.noteAligns[`${t}:${k}`]) || null);
          }
          setClipboard({ cells, notes, aligns, srcTrack: t });
          break;
        }
        e.preventDefault();
      } else if (e.key === 'v' || e.key === 'V') {
        if (!clipboard || !onPasteBlock) return;
        // Paste at the anchor cell, or first selected cell, or default to track 0 / slot 0
        let dstTrack = clipboard.srcTrack, dstStart = 0;
        if (anchor) { dstTrack = anchor.t; dstStart = anchor.i; }
        else if (selection.size > 0) {
          const k = [...selection][0]; const [tt, ii] = k.split(':').map(Number);
          dstTrack = tt; dstStart = ii;
        }
        onPasteBlock(clipboard, dstTrack, dstStart);
        // Select the pasted range
        const len = clipboard.cells.length;
        const next = new Set();
        for (let k = 0; k < len; k++) next.add(`${dstTrack}:${dstStart + k}`);
        setSelection(next);
        setAnchor({ kind: 't', t: dstTrack, i: dstStart });
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Cell sizing already declared up top with unified helpers; nothing to do here.

  // Compute selection details — keep today and prev separately so SelectionDetail can render both.
  const selByTrack = { 0: [], 1: [], 2: [] };           // today-idx values
  const selByTrackPrev = { 0: [], 1: [], 2: [] };       // prev arrIdx (0..2)
  selection.forEach(k => {
    if (k.startsWith('p:')) {
      const parts = k.split(':');
      selByTrackPrev[+parts[1]].push(+parts[2]);
    } else {
      const [t, i] = k.split(':').map(Number);
      selByTrack[t].push(i);
    }
  });

  return (
    <React.Fragment>
      <div className="daily-grid">
        {/* 移动端拖拽多选起手＝长按格子，禁掉浏览器/广告插件的长按上下文菜单（如"手动标记广告"） */}
        <div className={`daily-table ${locked ? 'tt-locked' : ''}`} ref={tableRef} onContextMenu={(e) => e.preventDefault()}>
          <div className="time-axis">
            <button
              className={`prev-toggle ${expandPrev ? 'on' : ''}`}
              onClick={() => setExpandPrev(v => !v)}
              title={expandPrev ? '收起昨晚 21-23 点' : '展开昨晚 21-23 点'}
            >
              {expandPrev ? '‹昨' : '昨›'}
            </button>
            {expandPrev && (
              <div className="time-seg prev-seg" style={{ width: prevTailWidth }}>昨晚</div>
            )}
            {TIME_SEGMENTS.map(seg => {
              let w = 0;
              SLOTS.forEach(s => {
                if (s.h >= seg.range[0] && s.h <= seg.range[1]) w += slotW(s);
              });
              return <div key={seg.label} className="time-seg" style={{ width: w }}>{seg.label}</div>;
            })}
          </div>
          <div className="rec-row">
            <div className="row-label" />
            <div className="row-cells">
              {expandPrev && PREV_TAIL_HOURS.map(h => (
                <div key={`pb-${h}`} className="rec-band tone-sleep prev-band" style={{ width: prevHourW(h) }}>
                  <span className="rec-label">{({ 21: '瑜', 22: '准备睡觉', 23: '睡觉' })[h] || ''}</span>
                </div>
              ))}
              {RECOMMEND_BANDS.map(b => {
                let w = 0;
                for (let h = b.from; h <= b.to; h++) {
                  const hg = HOURS_FOR_HEADER.find(x => x.h === h);
                  if (hg) w += hg.idxs.reduce((a, i) => a + slotW(SLOTS[i]), 0);
                }
                return (
                  <div key={b.from} className={`rec-band tone-${b.tone}`} style={{ width: w }}>
                    <span className="rec-label">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="hour-row hour-row-bg">
            <div className="row-label" />
            <div className="row-cells">
              {expandPrev && PREV_TAIL_HOURS.map(h => (
                <div key={`ph-${h}`} className="hour-group hour-tone-sleep prev-hour" style={{ width: prevHourW(h) }}>
                  <div className="hour-num">{h}</div>
                </div>
              ))}
              {HOURS_FOR_HEADER.map(hg => {
                const band = RECOMMEND_BANDS.find(b => hg.h >= b.from && hg.h <= b.to);
                const tone = band ? band.tone : 'focus';
                return (
                  <div key={hg.h} className={`hour-group hour-tone-${tone}`} style={{ width: hg.idxs.reduce((a, i) => a + slotW(SLOTS[i]), 0) }}>
                    <div className="hour-num">{hg.h}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {[0, 1, 2].map(t => (
            <div key={t} className="track-row" style={{ minHeight: unit + 4 }}>
              <div className="row-label">{TRACK_LABELS[t]}</div>
              <div className="row-cells">
                {expandPrev && prevSlotIdxs.map((psIdx, p) => {
                  const arrIdx = p; // prev cells: arrIdx 0..PREV_LEN-1（21:00 / 22:00 / 22:30 / 23:00 / 23:30）
                  const ps = SLOTS[psIdx];
                  const groupBreakP = p > 0 && SLOTS[prevSlotIdxs[p - 1]].h !== ps.h ? 'gb' : '';
                  const tLenP = moveDrag ? (moveDrag.srcEnd - moveDrag.srcStart + 1) : 0;
                  const inSrcP = moveDrag && t === moveDrag.srcTrack && p >= moveDrag.srcStart && p <= moveDrag.srcEnd;
                  const inTgtP = moveDrag && t === moveDrag.hoverTrack && p >= moveDrag.hoverArrIdx && p < moveDrag.hoverArrIdx + tLenP;
                  const pcat = (prevDay && prevDay.tracks && prevDay.tracks[t] && prevDay.tracks[t][psIdx]) || null;
                  const pc = pcat ? (CAT_BY_ID[pcat] || null) : null;
                  const psel = selection.has(prevKey(t, p));
                  const pnote = (prevDay && prevDay.notes && prevDay.notes[`${t}:${psIdx}`]) || null;
                  const pAlign = (prevDay && prevDay.noteAligns && prevDay.noteAligns[`${t}:${psIdx}`]) || 'left';
                  const pUseBlock = pc && pc.block;
                  const m = mergeFor(t, arrIdx);
                  const isMergeStart = m && m.start === arrIdx;
                  const isMergeEnd = m && m.end === arrIdx;
                  const isMergeMid = m && !isMergeStart && !isMergeEnd;
                  const isRunStart = !m || isMergeStart;
                  let displayNote = null;
                  let displayAlign = 'left';
                  let displayTrunc = 'ellipsis';
                  let noteSpanW = null;
                  let mergeHasNote = false;
                  if (m) {
                    for (let a = m.start; a <= m.end; a++) {
                      if (getCellNote(t, a)) { mergeHasNote = true; break; }
                    }
                  }
                  if (isRunStart) {
                    if (m) {
                      for (let a = m.start; a <= m.end; a++) {
                        const n = getCellNote(t, a);
                        if (n) { displayNote = n; displayAlign = getCellAlign(t, a); displayTrunc = getCellTruncate(t, a); break; }
                      }
                      if (displayNote) {
                        noteSpanW = 0;
                        for (let a = m.start; a <= m.end; a++) noteSpanW += getCellWidth(a);
                      }
                    } else if (pnote) {
                      displayNote = pnote;
                      displayAlign = pAlign;
                      displayTrunc = (prevDay && prevDay.noteTruncates && prevDay.noteTruncates[`${t}:${psIdx}`]) || 'ellipsis';
                      noteSpanW = slotW(ps);
                    }
                  }
                  return (
                    <div
                      key={`p-${p}`}
                      data-cell-key={`p:${t}:${p}`}
                      className={`cell prev-cell ${groupBreakP} ${psel ? 'sel' : ''} ${pUseBlock ? 'has-block' : ''} ${m ? 'merged' : ''} ${isMergeStart ? 'merge-start' : ''} ${isMergeEnd ? 'merge-end' : ''} ${isMergeMid ? 'merge-mid' : ''} ${displayNote ? 'has-note' : ''} ${inSrcP ? 'drag-src' : ''} ${inTgtP ? 'drag-tgt' : ''} ${locatedKeys.has(`p:${t}:${p}`) ? 'cell-locate-flash' : ''}`}
                      style={{
                        width: slotW(ps), height: unit,
                        background: pUseBlock ? pc.color : (pc ? `color-mix(in oklch, ${pc.color} 10%, transparent)` : 'transparent'),
                      }}
                      onMouseDown={(e) => onPrevCellDown(t, p, e)}
                      onMouseEnter={() => onPrevCellEnter(t, p)}
                      title={pc ? `昨日 ${pad(ps.h)}:${pad(ps.m)}  ${pc.label}${pnote ? ' · ' + pnote : ''}` : `昨日 ${pad(ps.h)}:${pad(ps.m)}`}
                    >
                      {pc && !pUseBlock && !isMergeMid && !displayNote && !mergeHasNote && (
                        <span className="cell-text" style={{ color: pc.color }}>{pc.short}</span>
                      )}
                      {displayNote && (
                        <span className={`cell-note cn-${displayAlign} cn-trunc-${displayTrunc}`} style={{ color: pUseBlock ? 'var(--note-on-block)' : (pc ? pc.color : 'var(--text-dim)'), width: noteSpanW }}>{displayNote}</span>
                      )}
                    </div>
                  );
                })}
                {SLOTS.map((s, i) => {
                  const arrIdx = todayIdxToArrIdx(i);
                  const cat = day.tracks[t][i];
                  const c = cat ? (CAT_BY_ID[cat] || null) : null;
                  const sel = isSelected(t, i);
                  const note = day.notes[`${t}:${i}`];
                  const useBlock = c && c.block;
                  const groupBreak = i > 0 && SLOTS[i - 1].h !== s.h ? 'gb' : '';
                  const m = mergeFor(t, arrIdx);
                  const isMergeStart = m && m.start === arrIdx;
                  const isMergeEnd = m && m.end === arrIdx;
                  const isMergeMid = m && !isMergeStart && !isMergeEnd;
                  const isRunStart = !m || isMergeStart;
                  let displayNote = null;
                  let displayAlign = 'left';
                  let displayTrunc = 'ellipsis';
                  let noteSpanW = null;
                  let mergeHasNote = false;
                  if (m) {
                    for (let a = m.start; a <= m.end; a++) {
                      if (getCellNote(t, a)) { mergeHasNote = true; break; }
                    }
                  }
                  // Drag state: is this cell the drag source / drag target? (all coords in arrIdx)
                  const curArr = todayIdxToArrIdx(i);
                  const inSrc = moveDrag && t === moveDrag.srcTrack && curArr >= moveDrag.srcStart && curArr <= moveDrag.srcEnd;
                  const tLen = moveDrag ? (moveDrag.srcEnd - moveDrag.srcStart + 1) : 0;
                  const inTgt = moveDrag && t === moveDrag.hoverTrack && curArr >= moveDrag.hoverArrIdx && curArr < moveDrag.hoverArrIdx + tLen;
                  const isDraggable = !!c;
                  if (isRunStart) {
                    if (m) {
                      for (let a = m.start; a <= m.end; a++) {
                        const n = getCellNote(t, a);
                        if (n) { displayNote = n; displayAlign = getCellAlign(t, a); displayTrunc = getCellTruncate(t, a); break; }
                      }
                      if (displayNote) {
                        noteSpanW = 0;
                        for (let a = m.start; a <= m.end; a++) noteSpanW += getCellWidth(a);
                      }
                    } else if (note) {
                      displayNote = note;
                      displayAlign = (day.noteAligns && day.noteAligns[`${t}:${i}`]) || 'left';
                      displayTrunc = (day.noteTruncates && day.noteTruncates[`${t}:${i}`]) || 'ellipsis';
                      noteSpanW = slotW(s);
                    }
                  }
                  const cellKey = `${t}:${i}`;
                  const isLocated = locatedKeys.has(cellKey);
                  return (
                    <div
                      key={i}
                      data-cell-key={cellKey}
                      className={`cell ${sel ? 'sel' : ''} ${groupBreak} ${useBlock ? 'has-block' : ''} ${m ? 'merged' : ''} ${isMergeStart ? 'merge-start' : ''} ${isMergeEnd ? 'merge-end' : ''} ${isMergeMid ? 'merge-mid' : ''} ${displayNote ? 'has-note' : ''} ${isDraggable ? 'draggable' : ''} ${inSrc ? 'drag-src' : ''} ${inTgt ? 'drag-tgt' : ''} ${isLocated ? 'cell-locate-flash' : ''}`}
                      style={{
                        width: slotW(s), height: unit,
                        background: useBlock ? c.color : (c ? `color-mix(in oklch, ${c.color} 10%, transparent)` : 'transparent'),
                      }}
                      onMouseDown={(e) => onCellDown(t, i, e)}
                      onMouseEnter={() => onCellEnter(t, i)}
                      title={(() => {
                        const base = c ? c.label + (note ? ' · ' + note : '') : SLOTS[i].label;
                        const pl = pomoCellMap[`${t}:${i}`];
                        return pl ? `${base}\n🍅 ${formatPomoRange(pl)}` : base;
                      })()}
                    >
                      {c && !useBlock && !isMergeMid && !displayNote && !mergeHasNote && (
                        <span className="cell-text" style={{ color: c.color }}>{c.short}</span>
                      )}
                      {displayNote && (
                        <span className={`cell-note cn-${displayAlign} cn-trunc-${displayTrunc}`} style={{ color: useBlock ? 'var(--note-on-block)' : (c ? c.color : 'var(--text-dim)'), width: noteSpanW }}>{displayNote}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <SelectionDetail
        selection={selection}
        selByTrack={selByTrack}
        selByTrackPrev={selByTrackPrev}
        day={day}
        prevDay={prevDay}
        prevSlotIdxs={prevSlotIdxs}
        expandPrev={expandPrev}
        unifiedLen={unifiedLen}
        onPrevNotes={onPrevNotes}
        onPrevCellSet={onPrevCellSet}
        onClearSelection={() => setSelection(new Set())}
        onClearCells={clearSelectedCells}
        onNote={onNote}
        onNotes={onNotes}
        canUndo={canUndo}
        onUndo={onUndo}
        onApply={(catId) => applyToSelection(catId)}
        quickApplyIds={quickApplyIds}
        onQuickApplyChange={onQuickApplyChange}
        quickApplyCollapsed={quickApplyCollapsed}
        onQuickApplyCollapsedChange={onQuickApplyCollapsedChange}
        defaultQuickApplyIds={defaultQuickApplyIds}
        customCats={customCats}
        catOrder={catOrder}
        onLocateBlock={(t, arrs) => {
          if (!arrs || arrs.length === 0) return;
          // 计算所有要闪烁的 cell key（today 用 t:i，prev 用 p:t:p）
          const keys = new Set();
          arrs.forEach(a => {
            if (a < PREV_LEN && expandPrev) keys.add(`p:${t}:${a}`);
            else {
              const i = expandPrev ? a - PREV_LEN : a;
              keys.add(`${t}:${i}`);
            }
          });
          setLocatedKeys(keys);
          // 滚动第一个 cell 到视口水平中心
          const firstKey = [...keys][0];
          requestAnimationFrame(() => {
            const el = document.querySelector(`[data-cell-key="${CSS.escape(firstKey)}"]`);
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          });
          // 1.8s 后清除高亮
          if (locateTimerRef.current) clearTimeout(locateTimerRef.current);
          locateTimerRef.current = setTimeout(() => setLocatedKeys(new Set()), 1800);
        }}
        onRemoveTrack={(t, arrs) => {
          // 如果传了 arrs（按 block 范围），就只清这部分；否则清整条轨道（兼容旧调用）
          const todayEntries = [];
          const prevEntries = [];
          const next = new Set(selection);
          if (Array.isArray(arrs) && arrs.length > 0) {
            arrs.forEach(a => {
              if (a < PREV_LEN && expandPrev) {
                const psIdx = prevSlotIdxs[a];
                const key = `p:${t}:${a}`;
                if (next.has(key)) {
                  prevEntries.push({ track: t, idx: psIdx, cat: null });
                  next.delete(key);
                }
              } else {
                const i = expandPrev ? a - PREV_LEN : a;
                const key = `${t}:${i}`;
                if (next.has(key)) {
                  todayEntries.push({ track: t, idx: i, cat: null });
                  next.delete(key);
                }
              }
            });
          } else {
            [...next].forEach(k => {
              if (k.startsWith(`p:${t}:`)) {
                const parts = k.split(':');
                const p = +parts[2];
                prevEntries.push({ track: t, idx: prevSlotIdxs[p], cat: null });
                next.delete(k);
              } else if (k.startsWith(`${t}:`)) {
                const [tr, i] = k.split(':').map(Number);
                todayEntries.push({ track: tr, idx: i, cat: null });
                next.delete(k);
              }
            });
          }
          if (todayEntries.length && onCellSet) onCellSet(todayEntries);
          if (prevEntries.length && onPrevCellSet) onPrevCellSet(prevEntries);
          setSelection(next);
        }}
        onMoveTrack={(srcTrack, arrs, dstTrack, cat) => {
          // 移动端拖拽换轴难 → 点详情卡「X轴」文字直接把整段移到目标轴（同时段同分类）
          if (srcTrack === dstTrack || !Array.isArray(arrs) || arrs.length === 0) return;
          const arrToCell = (a) =>
            (a < PREV_LEN && expandPrev)
              ? { isPrev: true, idx: prevSlotIdxs[a] }
              : { isPrev: false, idx: arrIdxToTodayIdx(a) };
          const cells = arrs.map(arrToCell);
          // 1) 冲突校验：目标轨道这些格子必须全空，否则整段不移（与 N32 错轨提示口径一致）
          const occupied = cells.some(c => {
            const grid = c.isPrev ? (prevDay && prevDay.tracks && prevDay.tracks[dstTrack]) : day.tracks[dstTrack];
            return grid && grid[c.idx] != null;
          });
          if (occupied) {
            window.alert(`「${TRACK_LABELS[dstTrack]}轴」这段时间已经有数据了，整段没有移动。先清空目标轨道再试。`);
            return;
          }
          // 2) 清原轨道 + 写目标轨道（同位置同分类）
          const todayEntries = [];
          const prevEntries = [];
          cells.forEach(c => {
            const bucket = c.isPrev ? prevEntries : todayEntries;
            bucket.push({ track: srcTrack, idx: c.idx, cat: null });
            bucket.push({ track: dstTrack, idx: c.idx, cat });
          });
          if (todayEntries.length && onCellSet) onCellSet(todayEntries);
          if (prevEntries.length && onPrevCellSet) onPrevCellSet(prevEntries);
          // 2.5) 番茄跟随换轴：把挂在这段上的番茄 appliedTrack 迁到目标轴（否则脱钩消失，同 B4）
          if (onMigratePomoTrack) {
            const todaySlots = cells.filter(c => !c.isPrev).map(c => c.idx);
            const prevSlots = cells.filter(c => c.isPrev).map(c => c.idx);
            onMigratePomoTrack(srcTrack, todaySlots, prevSlots, dstTrack);
          }
          // 3) 迁移备注：原轨道每个格子的备注 → 目标轨道同位置
          cells.forEach(c => {
            const srcDay = c.isPrev ? prevDay : day;
            const noteKey = `${srcTrack}:${c.idx}`;
            const note = srcDay && srcDay.notes && srcDay.notes[noteKey];
            if (!note) return;
            const align = (srcDay.noteAligns && srcDay.noteAligns[noteKey]) || 'left';
            const setN = c.isPrev ? onPrevNotes : onNotes;
            if (!setN) return;
            setN([{ t: srcTrack, i: c.idx }], '', 'left');
            setN([{ t: dstTrack, i: c.idx }], note, align);
          });
          // 4) 选区：原轨道 key → 目标轨道 key
          setSelection(prev => {
            const next = new Set(prev);
            arrs.forEach(a => {
              if (a < PREV_LEN && expandPrev) {
                next.delete(`p:${srcTrack}:${a}`);
                next.add(`p:${dstTrack}:${a}`);
              } else {
                const i = expandPrev ? a - PREV_LEN : a;
                next.delete(`${srcTrack}:${i}`);
                next.add(`${dstTrack}:${i}`);
              }
            });
            return next;
          });
        }}
        onResizeRange={(track, oldStartArr, oldEndArr, newStartArr, newEndArr, catId) => {
          if (newStartArr > newEndArr) return;
          // 番茄校验：仅当 resize 整段都在今日时（不含 prev 折叠区）才检查
          const oldAllToday = !expandPrev || (oldStartArr >= 3 && oldEndArr >= 3);
          const newAllToday = !expandPrev || (newStartArr >= 3 && newEndArr >= 3);
          if (oldAllToday && newAllToday && onValidateBlockMove) {
            const todayOldStart = expandPrev ? oldStartArr - PREV_LEN : oldStartArr;
            const todayOldEnd = expandPrev ? oldEndArr - PREV_LEN : oldEndArr;
            const todayNewStart = expandPrev ? newStartArr - PREV_LEN : newStartArr;
            const todayNewEnd = expandPrev ? newEndArr - PREV_LEN : newEndArr;
            const res = onValidateBlockMove(track, todayOldStart, todayOldEnd, todayNewStart, todayNewEnd);
            if (res && !res.ok) {
              window.alert(res.message);
              return;
            }
          }
          const arrToCell = (arr) =>
            (arr < PREV_LEN && expandPrev)
              ? { isPrev: true, idx: prevSlotIdxs[arr] }
              : { isPrev: false, idx: arrIdxToTodayIdx(arr) };
          const oldSet = new Set();
          for (let a = oldStartArr; a <= oldEndArr; a++) oldSet.add(a);
          const newSet = new Set();
          for (let a = newStartArr; a <= newEndArr; a++) newSet.add(a);
          const todayEntries = [];
          const prevEntries = [];
          [...oldSet].filter(a => !newSet.has(a)).forEach(a => {
            const c = arrToCell(a);
            (c.isPrev ? prevEntries : todayEntries).push({ track, idx: c.idx, cat: null });
          });
          if (catId) [...newSet].forEach(a => {
            const c = arrToCell(a);
            (c.isPrev ? prevEntries : todayEntries).push({ track, idx: c.idx, cat: catId });
          });
          if (todayEntries.length && onCellSet) onCellSet(todayEntries);
          if (prevEntries.length && onPrevCellSet) onPrevCellSet(prevEntries);
          // Selection → new range
          const next = new Set();
          for (let a = newStartArr; a <= newEndArr; a++) {
            if (a < PREV_LEN && expandPrev) next.add(prevKey(track, a));
            else next.add(`${track}:${arrIdxToTodayIdx(a)}`);
          }
          setSelection(next);
          // Migrate note from old start to new start if changed
          if (oldStartArr !== newStartArr) {
            const oldC = arrToCell(oldStartArr);
            const newC = arrToCell(newStartArr);
            const oldDay = oldC.isPrev ? prevDay : day;
            const noteKey = `${track}:${oldC.idx}`;
            const oldNote = oldDay && oldDay.notes && oldDay.notes[noteKey];
            const oldAlign = oldDay && oldDay.noteAligns && oldDay.noteAligns[noteKey];
            if (oldNote) {
              // Clear old; write to new (use Notes variants for atomicity)
              if (oldC.isPrev) onPrevNotes && onPrevNotes([{ t: track, i: oldC.idx }], '', 'left');
              else onNotes && onNotes([{ t: track, i: oldC.idx }], '', 'left');
              if (newC.isPrev) onPrevNotes && onPrevNotes([{ t: track, i: newC.idx }], oldNote, oldAlign || 'left');
              else onNotes && onNotes([{ t: track, i: newC.idx }], oldNote, oldAlign || 'left');
            }
          }
        }}
      />
    </React.Fragment>
  );
}

function SelectionDetail({ selection, selByTrack, selByTrackPrev, day, onClearSelection, onClearCells, onNote, onNotes, onApply, onRemoveTrack, onLocateBlock, onResizeRange, onMoveTrack, prevDay, prevSlotIdxs, expandPrev, unifiedLen, onPrevNotes, onPrevCellSet, canUndo, onUndo, quickApplyIds, onQuickApplyChange, quickApplyCollapsed: quickApplyCollapsedProp, onQuickApplyCollapsedChange, defaultQuickApplyIds, customCats, catOrder }) {
  const [showQuickApplyEditor, setShowQuickApplyEditor] = React.useState(false);
  // 昨晚尾格数：从 prevSlotIdxs 派生（DailyTable 里的 PREV_LEN 不在本组件作用域，必须本地算一份）
  const PREV_LEN = (prevSlotIdxs && prevSlotIdxs.length) || 0;
  // 快速应用分类卡片折叠态（撤销按钮后的 ▾/▸ 切换；折叠隐藏分类格+备注）。受控于 App 并持久化
  const quickApplyCollapsed = !!quickApplyCollapsedProp;
  const setQuickApplyCollapsed = (next) =>
    onQuickApplyCollapsedChange && onQuickApplyCollapsedChange(typeof next === 'function' ? next(quickApplyCollapsed) : next);
  // 轨道选择器：点详情卡「X轴」文字弹居中弹窗换轴。null=未开，否则 { t, arrs, cat }
  const [trackPicker, setTrackPicker] = React.useState(null);
  // 哪些番茄日志的"暂停明细"被展开（默认全部折叠，点 ⏸ N ▾ 徽标展开）
  const [expandedPomos, setExpandedPomos] = React.useState(() => new Set());
  function togglePomoExpand(id) {
    setExpandedPomos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  // Collect (kind, t, i) entries — supports both today's slots and prev-tail slots
  const items = [...selection];
  const noteTargets = React.useMemo(() => {
    const out = [];
    items.forEach(k => {
      if (k.startsWith('p:')) {
        const parts = k.split(':');
        const t = +parts[1], p = +parts[2];
        const psIdx = prevSlotIdxs ? prevSlotIdxs[p] : null;
        if (psIdx != null) out.push({ kind: 'p', t, i: psIdx });
      } else {
        const [t, i] = k.split(':').map(Number);
        out.push({ kind: 't', t, i });
      }
    });
    return out.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'p' ? -1 : 1) || a.t - b.t || a.i - b.i);
  }, [selection]);
  // Pre-fill with existing note from the first selected target that has one
  const noteVal = React.useMemo(() => {
    for (const tgt of noteTargets) {
      const src = tgt.kind === 'p' ? (prevDay && prevDay.notes) : day.notes;
      if (src && src[`${tgt.t}:${tgt.i}`]) return src[`${tgt.t}:${tgt.i}`];
    }
    return '';
  }, [noteTargets, day.notes, prevDay && prevDay.notes]);
  const noteAlignVal = React.useMemo(() => {
    for (const tgt of noteTargets) {
      const src = tgt.kind === 'p' ? (prevDay && prevDay.noteAligns) : day.noteAligns;
      if (src && src[`${tgt.t}:${tgt.i}`]) return src[`${tgt.t}:${tgt.i}`];
    }
    return 'left';
  }, [noteTargets, day.noteAligns, prevDay && prevDay.noteAligns]);
  const noteTruncateVal = React.useMemo(() => {
    for (const tgt of noteTargets) {
      const src = tgt.kind === 'p' ? (prevDay && prevDay.noteTruncates) : day.noteTruncates;
      if (src && src[`${tgt.t}:${tgt.i}`]) return src[`${tgt.t}:${tgt.i}`];
    }
    return 'ellipsis';
  }, [noteTargets, day.noteTruncates, prevDay && prevDay.noteTruncates]);
  const [noteEdit, setNoteEdit] = React.useState('');
  const [alignEdit, setAlignEdit] = React.useState('left');
  const [truncateEdit, setTruncateEdit] = React.useState('ellipsis');
  // 草稿与已保存值不同 → 显示「● 未保存」
  const noteDirty = noteEdit !== noteVal || alignEdit !== noteAlignVal || truncateEdit !== noteTruncateVal;
  // 选区身份 key：草稿必须按「选的是哪一格」重置，而不是按 noteVal/格数。否则切到
  // 另一个同样空备注、同样格数的格子时 effect 不触发，没保存的草稿会串过去。
  const selKey = React.useMemo(() => noteTargets.map(t => `${t.kind}${t.t}:${t.i}`).join(','), [noteTargets]);
  const noteMirrorRef = React.useRef({ note: '', align: 'left', truncate: 'ellipsis' });
  const noteCtxRef = React.useRef({ selKey: '', targets: [], base: '', baseAlign: 'left', baseTruncate: 'ellipsis' });

  function saveNoteTo(targets, val, align, truncate) {
    const todays = targets.filter(x => x.kind === 't').map(({ t, i }) => ({ t, i }));
    const prevs = targets.filter(x => x.kind === 'p').map(({ t, i }) => ({ t, i }));
    if (todays.length) {
      if (onNotes) onNotes(todays, val, align, truncate);
      else todays.forEach(({ t, i }) => onNote(t, i, val, align, truncate));
    }
    if (prevs.length && onPrevNotes) onPrevNotes(prevs, val, align, truncate);
  }
  function saveNote(val, align, truncate) {
    saveNoteTo(noteTargets, val, align, truncate);
    // 落库后更新基线，避免再被判成「未保存」或切走时误弹
    noteMirrorRef.current = { note: val, align, truncate };
    noteCtxRef.current = { ...noteCtxRef.current, base: val, baseAlign: align, baseTruncate: truncate };
  }

  // 草稿重置：① 选区切换 → 重置成新格已存值（修「串到别的格子」），上一格有未保存草稿先弹确认；
  //          ② 同一选区但已存值变了（撤销/重做/外部）→ 草稿没在编辑时跟随刷新，
  //             这样「撤销上一次保存备注」能反映回输入框（否则只改数据、输入框留旧值显示未保存）。
  React.useEffect(() => {
    const ctx = noteCtxRef.current;
    const m = noteMirrorRef.current;
    const draftDirty = m.note !== ctx.base || m.align !== ctx.baseAlign || m.truncate !== ctx.baseTruncate;
    const applyReset = () => {
      setNoteEdit(noteVal); setAlignEdit(noteAlignVal); setTruncateEdit(noteTruncateVal);
      noteMirrorRef.current = { note: noteVal, align: noteAlignVal, truncate: noteTruncateVal };
      noteCtxRef.current = { selKey, targets: noteTargets, base: noteVal, baseAlign: noteAlignVal, baseTruncate: noteTruncateVal };
    };
    if (ctx.selKey !== selKey) {
      if (ctx.selKey && draftDirty && typeof window !== 'undefined' &&
          window.confirm('上一格的备注还没保存，要保存吗？\n确定 = 保存，取消 = 丢弃')) {
        saveNoteTo(ctx.targets, m.note, m.align, m.truncate);
      }
      applyReset();
    } else if (!draftDirty) {
      applyReset();
    } else {
      noteCtxRef.current = { ...ctx, base: noteVal, baseAlign: noteAlignVal, baseTruncate: noteTruncateVal };
    }
  }, [selKey, noteVal, noteAlignVal, noteTruncateVal]);

  // arrIdx ↔ slot/hour info helper (works for both prev tail and today slots)
  function arrInfo(arr) {
    if (arr < PREV_LEN && expandPrev) return SLOTS[prevSlotIdxs[arr]]; // 昨晚尾：取真实 slot（含半点 span）
    const todayIdx = expandPrev ? arr - PREV_LEN : arr;
    return SLOTS[todayIdx];
  }
  // Continuous-time coord (in hours, where today midnight = 0; prev tail = -3..-1).
  // Used for "±Nh" range checks that don't break at the midnight wrap.
  function continuousTime(arr) {
    const info = arrInfo(arr);
    if (!info) return 0;
    if (arr < PREV_LEN && expandPrev) return info.h + (info.m || 0) / 60 - 24; // 昨晚尾 → 负值（含半点分钟）
    return info.h + (info.m || 0) / 60;
  }
  // Translate today-idx / prev-arr to unified arrIdx
  const todayToArr = (i) => expandPrev ? i + PREV_LEN : i;
  // Build per-track time range + dominant categories (merge today + prev tail in arrIdx)
  // 每个"连续同分类块"一张卡：先按 arrIdx 排序，遇到 cat 变化或 arrIdx 不连续就切一个 block
  const trackInfos = [];
  for (let t = 0; t < 3; t++) {
    const idxs = selByTrack[t] || [];
    const prevIdxs = (selByTrackPrev && selByTrackPrev[t]) || [];
    if (idxs.length === 0 && prevIdxs.length === 0) continue;
    // 收集 (arrIdx, cat) tuple
    const tuples = [];
    idxs.forEach(i => {
      tuples.push({ arr: todayToArr(i), cat: day.tracks[t][i] || null });
    });
    prevIdxs.forEach(p => {
      const psIdx = prevSlotIdxs ? prevSlotIdxs[p] : null;
      const cat = (psIdx != null && prevDay && prevDay.tracks && prevDay.tracks[t]) ? prevDay.tracks[t][psIdx] : null;
      tuples.push({ arr: p, cat: cat || null });
    });
    tuples.sort((a, b) => a.arr - b.arr);
    // 切 block
    let cur = null;
    const flush = () => {
      if (!cur) return;
      const arrs = cur.arrs;
      let hours = 0;
      arrs.forEach(a => {
        if (a < PREV_LEN && expandPrev) hours += SLOTS[prevSlotIdxs[a]].span; // 昨晚尾按 span（半点=0.5）
        else {
          const todayIdx = expandPrev ? a - PREV_LEN : a;
          hours += SLOTS[todayIdx].span;
        }
      });
      const prevPart = arrs.filter(a => a < PREV_LEN && expandPrev);
      const todayPart = arrs.filter(a => !(a < PREV_LEN && expandPrev)).map(a => expandPrev ? a - PREV_LEN : a);
      const prevLabel = prevPart.length > 0 ? formatPrevTailRange(prevPart, prevSlotIdxs) : '';
      const todayLabel = todayPart.length > 0 ? formatTimeRange(todayPart) : '';
      const ranges = [prevLabel, todayLabel].filter(Boolean).join(' / ');
      const cats = cur.cat ? { [cur.cat]: hours } : {};
      trackInfos.push({
        t,
        count: arrs.length,
        hours,
        ranges,
        cats,
        contiguous: true,
        startArr: arrs[0],
        endArr: arrs[arrs.length - 1],
        arrs: [...arrs],   // 该 block 在 unified arrIdx 坐标下的所有格子（用于"删"按钮按 block 范围移除）
        dominantCat: cur.cat,
      });
      cur = null;
    };
    for (let k = 0; k < tuples.length; k++) {
      const tup = tuples[k];
      if (!cur) {
        cur = { cat: tup.cat, arrs: [tup.arr] };
      } else if (tup.arr === cur.arrs[cur.arrs.length - 1] + 1 && tup.cat === cur.cat) {
        cur.arrs.push(tup.arr);
      } else {
        flush();
        cur = { cat: tup.cat, arrs: [tup.arr] };
      }
    }
    flush();
  }

  // Build option arrays for EditableRange, scoped to ±6h around the current value (微调).
  // Uses continuous-time so midnight wrap doesn't break the range (e.g., 23:00 ±6h
  // correctly covers 17:00 prev → 5:00 today).
  function buildOpts(startArr, endArr) {
    if (startArr == null) return { startOpts: [], endOpts: [] };
    const tStart = continuousTime(startArr);
    const tEnd = endArr != null ? continuousTime(endArr) : tStart;
    const startOpts = [];
    const endOpts = [];
    for (let a = 0; a < unifiedLen; a++) {
      // 落在「起点 ±6h」∪「终点 ±6h」内才入选 —— 保证两端点本身一定在选项里（块 >6h 时旧逻辑会把
      // 终点排除，导致 <select> value 无匹配 option、退回显示第一项 01:00），各端再留 ±6h 微调。
      if (Math.abs(continuousTime(a) - tStart) > 6 && Math.abs(continuousTime(a) - tEnd) > 6) continue;
      const s = arrInfo(a);
      startOpts.push({ value: a, label: `${pad(s.h)}:${pad(s.m)}` });
      const endM = s.m + (s.span === 0.5 ? 30 : 60);
      const eh = endM >= 60 ? s.h + 1 : s.h;
      const em = endM >= 60 ? endM - 60 : endM;
      endOpts.push({ value: a, label: `${pad(eh)}:${pad(em)}` });
    }
    return { startOpts, endOpts };
  }

  const empty = selection.size === 0;
  return (
    <div className={`detail-bar ${empty ? 'empty' : ''}`}>
      {empty ? (
        <div className="detail-empty">
          <span className="detail-hint">点击格子查看详情 · 拖拽多选 · Shift+点击切换 · 数字 1-7 应用分类 · Esc 取消 · Del 清除</span>
        </div>
      ) : (
        <div className={`detail-content ${quickApplyCollapsed ? 'qa-collapsed' : ''}`}>
          <div className="detail-left">
            <div className="detail-head">
              <div className="detail-count">已选 <b>{selection.size}</b> 格</div>
              <div className="detail-actions">
                <button className="btn" onClick={onClearSelection}>取消选择</button>
                <button className="btn danger" onClick={onClearCells}>清除内容（拆分还原）</button>
              </div>
            </div>
            <div className="detail-tracks">
              {trackInfos.map((info, idx) => {
                if (!info) return null;
                return (
                  <div key={idx} className="detail-track" style={(() => {
                    const cids = Object.keys(info.cats);
                    const c = cids.length ? CAT_BY_ID[cids[0]] : null;
                    if (!c) {
                      // 未分类：灰色淡底，与有分类的卡同结构
                      return {
                        background: 'color-mix(in oklch, var(--text-dim) 10%, var(--card-bg))',
                        borderColor: 'color-mix(in oklch, var(--text-dim) 28%, var(--card-border))',
                        '--track-accent': 'var(--text-dim)',
                      };
                    }
                    return {
                      background: `color-mix(in oklch, ${c.color} 14%, var(--card-bg))`,
                      borderColor: `color-mix(in oklch, ${c.color} 38%, var(--card-border))`,
                      '--track-accent': c.color,
                    };
                  })()}>
                    <button
                      className="btn dt-locate"
                      title="在轨道上定位此时段（自动滚动 + 闪烁）"
                      onClick={() => onLocateBlock && onLocateBlock(info.t, info.arrs)}
                    ><span className="dt-btn-icon" aria-hidden="true">📍</span>定位</button>
                    <button className="btn danger dt-trash" title="从选择中移除此时段" onClick={() => onRemoveTrack(info.t, info.arrs)}>删</button>
                    <div className="dt-label">
                      {(() => {
                        const cids = Object.keys(info.cats);
                        const c = cids.length ? CAT_BY_ID[cids[0]] : null;
                        if (!c) {
                          // 未分类：与真实分类同布局，灰色胶囊（点 + 「未分类」+ 时长）
                          return (
                            <>
                              <span className="dt-label-dot dt-label-dot-uncat" />
                              <span className="dt-label-name dt-label-name-uncat">未分类</span>
                              <span className="dt-label-break" aria-hidden="true" />
                              <span className="dt-label-hours dt-label-hours-uncat">{info.hours}h</span>
                            </>
                          );
                        }
                        const h = info.cats[cids[0]];
                        return (
                          <>
                            <span className="dt-label-dot" style={{ background: c.color }} />
                            <span className="dt-label-name" style={{ color: c.color }}>{c.label}</span>
                            <span className="dt-label-break" aria-hidden="true" />
                            <span className="dt-label-hours">{h}h</span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="dt-range">
                      {info.contiguous ? (() => {
                        const { startOpts, endOpts } = buildOpts(info.startArr, info.endArr);
                        return (
                          <EditableRange
                            startValue={info.startArr}
                            endValue={info.endArr}
                            startOpts={startOpts}
                            endOpts={endOpts}
                            onChange={(newStart, newEnd) => onResizeRange(info.t, info.startArr, info.endArr, newStart, newEnd, info.dominantCat)}
                          />
                        );
                      })() : info.ranges}
                    </div>
                    {(() => {
                      // 番茄入轴时间范围（精确到秒）—— 跟时段编辑同行，narrow 时 flex-wrap 自动换行
                      const logs = [];
                      const todayLogs = ((day && day.pomoLogs) || []).filter(l => l.applied && l.appliedTrack === info.t);
                      const prevLogs = ((prevDay && prevDay.pomoLogs) || []).filter(l => l.applied && l.appliedTrack === info.t);
                      const toSlotSet = (startMs, endMs) => {
                        const sd = new Date(startMs);
                        const ed = new Date(endMs);
                        const startMin = sd.getHours() * 60 + sd.getMinutes() + sd.getSeconds() / 60;
                        const endMin = ed.getHours() * 60 + ed.getMinutes() + ed.getSeconds() / 60;
                        const set = new Set();
                        SLOTS.forEach((s, i) => {
                          const ss = s.h * 60 + s.m;
                          const se = ss + s.span * 60;
                          if (se > startMin && ss < endMin) set.add(i);
                        });
                        return set;
                      };
                      todayLogs.forEach(l => {
                        const slots = toSlotSet(l.startMs, l.endMs);
                        const hit = (info.arrs || []).some(a => {
                          if (a < PREV_LEN && expandPrev) return false;
                          const i = expandPrev ? a - PREV_LEN : a;
                          return slots.has(i);
                        });
                        if (hit) logs.push(l);
                      });
                      prevLogs.forEach(l => {
                        const slots = toSlotSet(l.startMs, l.endMs);
                        const hit = (info.arrs || []).some(a => {
                          if (!(a < PREV_LEN && expandPrev)) return false;
                          const psIdx = prevSlotIdxs ? prevSlotIdxs[a] : null;
                          return psIdx != null && slots.has(psIdx);
                        });
                        if (hit) logs.push(l);
                      });
                      if (logs.length === 0) return null;
                      const fmt = ms => {
                        const d = new Date(ms);
                        return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                      };
                      const fmtDur = m => (m >= 60 && m % 60 === 0) ? `${m/60}h` : `${m}m`;
                      const totalMin = (info.hours || 0) * 60;
                      const fmtPauses = pauses => pauses.map((p, i) => {
                        const sec = Math.max(1, Math.round((p.resumedAt - p.at) / 1000));
                        const kind = p.kind === 'sleep' ? '休眠' : '手动暂停';
                        return `${i + 1}. ${kind} ${fmt(p.at)} → ${fmt(p.resumedAt)}（${sec}s）`;
                      }).join('\n');
                      return (
                        <div className="dt-pomo-timeline">
                          {logs.map(l => {
                            const lc = CAT_BY_ID[l.catId];
                            const dotColor = lc ? lc.color : 'var(--accent)';
                            const pct = totalMin > 0 ? Math.min(100, (l.durationMin / totalMin) * 100) : 100;
                            const pctLabel = pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
                            // 展示前合并相邻 sleep（旧数据兜底）：next.at - prev.resumedAt <= 2000ms 视为同段
                            const mergedPauses = [];
                            (l.pauses || []).forEach(p => {
                              if (p.kind === 'sleep' && mergedPauses.length > 0) {
                                const last = mergedPauses[mergedPauses.length - 1];
                                if (last.kind === 'sleep' && (p.at - last.resumedAt) <= 2000) {
                                  mergedPauses[mergedPauses.length - 1] = { ...last, resumedAt: p.resumedAt };
                                  return;
                                }
                              }
                              mergedPauses.push(p);
                            });
                            const pauses = mergedPauses;
                            // 把 pauses 打散成事件流（manual 拆 2 条），按 at 升序
                            const events = [];
                            pauses.forEach((p, idx) => {
                              const sec = Math.max(1, Math.round((p.resumedAt - p.at) / 1000));
                              const fmtS = s => s >= 60 ? `${Math.floor(s/60)}m${s%60 ? s%60+'s' : ''}` : `${s}s`;
                              if (p.kind === 'sleep') {
                                events.push({ at: p.at, kind: 'sleep', icon: '💤', label: '系统休眠（计入）', time: `${fmt(p.at)} → ${fmt(p.resumedAt)}`, sec: fmtS(sec), key: `s-${idx}` });
                              } else {
                                events.push({ at: p.at, kind: 'mp', icon: '⏸', label: '暂停', time: fmt(p.at), key: `mp-${idx}` });
                                events.push({ at: p.resumedAt, kind: 'mr', icon: '▶', label: '继续', time: fmt(p.resumedAt), sec: fmtS(sec), key: `mr-${idx}` });
                              }
                            });
                            events.sort((a, b) => a.at - b.at);
                            const isExpanded = expandedPomos.has(l.id);
                            return (
                              <div key={l.id} className={`dt-pomo-node-wrap ${isExpanded ? 'expanded' : ''}`} style={{ '--pomo-dot': dotColor }}>
                                <div className="dt-pomo-node" style={{ '--pomo-pct': pct }}>
                                  <span className="dt-pomo-pie" data-pct={`占整段 ${pctLabel}%`} />
                                  <span className="dt-pomo-time">{fmt(l.startMs)} - {fmt(l.endMs)}</span>
                                  <span className="dt-pomo-tomato" aria-hidden="true">🍅</span>
                                  <span className="dt-pomo-dur">{fmtDur(l.durationMin)}</span>
                                  {l.catId && l.catId !== Object.keys(info.cats || {})[0] && (
                                    <button
                                      type="button"
                                      className="dt-pomo-switch"
                                      onClick={() => onApply(l.catId)}
                                      title={`把这段切换为「${(CAT_BY_ID[l.catId] && (CAT_BY_ID[l.catId].label || CAT_BY_ID[l.catId].name)) || l.catId}」`}
                                    >切换</button>
                                  )}
                                  {pauses.length > 0 && (
                                    <button
                                      type="button"
                                      className="dt-pomo-pauses"
                                      onClick={() => togglePomoExpand(l.id)}
                                      title={isExpanded ? '收起暂停明细' : '展开暂停明细'}
                                    >
                                      ⏸ {pauses.length}
                                      <span className="dt-pomo-pauses-arrow" aria-hidden="true">{isExpanded ? '▴' : '▾'}</span>
                                    </button>
                                  )}
                                </div>
                                {isExpanded && events.length > 0 && (
                                  <div className="dt-pomo-events">
                                    {events.map(e => (
                                      <div key={e.key} className={`dt-pomo-event kind-${e.kind}`}>
                                        <span className="dt-pomo-event-icon">{e.icon}</span>
                                        <span className="dt-pomo-event-label">{e.label}</span>
                                        <span className="dt-pomo-event-time">{e.time}</span>
                                        <span className="dt-pomo-event-sec">{e.sec || ''}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <div className="dt-meta">
                      <span className="dt-meta-count">共 {info.count} 格 · {info.hours} 小时</span>
                      <button
                        type="button"
                        className="dt-meta-track"
                        style={{ color: TRACK_FAMILY_COLOR[info.t] }}
                        title="换条轨道（移动端免拖拽）"
                        onClick={() => setTrackPicker({ t: info.t, arrs: info.arrs, cat: info.dominantCat })}
                      >{TRACK_LABELS[info.t]}轴 <span className="dt-meta-track-caret" aria-hidden="true">▾</span></button>
                    </div>
                    {(() => {
                      // 备注行 —— 行高随字数变化，按"可变高度放最下"放在最后
                      let blockNote = null;
                      for (const a of (info.arrs || [])) {
                        if (a < PREV_LEN && expandPrev) {
                          const psIdx = prevSlotIdxs ? prevSlotIdxs[a] : null;
                          const n = psIdx != null && prevDay && prevDay.notes && prevDay.notes[`${info.t}:${psIdx}`];
                          if (n) { blockNote = n; break; }
                        } else {
                          const i = expandPrev ? a - PREV_LEN : a;
                          const n = day.notes && day.notes[`${info.t}:${i}`];
                          if (n) { blockNote = n; break; }
                        }
                      }
                      if (!blockNote) return null;
                      return (
                        <div className="dt-note">
                          <span className="dt-note-label">备注</span>
                          <span className="dt-note-text">{blockNote}</span>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={`detail-right ${quickApplyCollapsed ? 'collapsed' : ''}`}>
            <div className="dr-tools">
              <button
                type="button"
                className="dr-undo"
                onClick={onUndo}
                disabled={!canUndo}
                title={canUndo ? '撤销上一次「应用分类」或「保存备注」' : '没有可撤销的操作'}
                aria-label="撤销">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 7h7a3.5 3.5 0 0 1 0 7H7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.5 4.5L3 7l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>撤销</span>
              </button>
            </div>
            <div className="dr-title">
              <button
                type="button"
                className="dr-collapse"
                onClick={() => setQuickApplyCollapsed(v => !v)}
                title={quickApplyCollapsed ? '展开快速应用分类' : '折叠快速应用分类'}
                aria-label={quickApplyCollapsed ? '展开快速应用分类' : '折叠快速应用分类'}
                aria-expanded={!quickApplyCollapsed}>{quickApplyCollapsed ? '›' : '‹'}</button>
              <span>快速应用分类</span>
            </div>
            {!quickApplyCollapsed && (
            <div className="dr-cats">
              {(quickApplyIds || []).map((id, idx) => {
                const c = CAT_BY_ID[id];
                if (!c) return null;
                // 显示键：内置 cat.key 优先；第 8/9 位 (idx 7/8) 动态绑 '8'/'9'
                const displayKey = c.key || (idx === 7 ? '8' : idx === 8 ? '9' : null);
                return (
                  <button
                    key={c.id}
                    className="dr-cat"
                    style={{ background: c.block ? c.color : 'transparent', color: c.block ? 'var(--swatch-text)' : c.color, borderColor: c.color }}
                    onClick={() => onApply(c.id)}
                    title={`${c.label}${displayKey ? ' (' + displayKey + ')' : ''}`}
                  >
                    {c.short}
                    {displayKey && <span className="dr-key">{displayKey}</span>}
                  </button>
                );
              })}
              <button
                className="dr-cat dr-cat-add"
                onClick={() => setShowQuickApplyEditor(true)}
                title="自定义快速应用分类"
              >+</button>
            </div>
            )}
            {!quickApplyCollapsed && noteTargets.length > 0 && (
              <div className="dr-note">
                <label className="dr-note-label">单元格备注（书名/剧名/活动）{noteTargets.length > 1 && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · 应用到选中 {noteTargets.length} 格</span>}</label>
                <div className="dr-note-align">
                  <span className="dr-note-align-label">对齐</span>
                  {['left', 'center', 'right'].map(a => (
                    <button
                      key={a}
                      type="button"
                      className={`dr-align-btn ${alignEdit === a ? 'on' : ''}`}
                      onClick={() => { setAlignEdit(a); noteMirrorRef.current.align = a; if (noteVal) saveNote(noteVal, a, truncateEdit); }}
                      title={a === 'left' ? '左对齐' : a === 'center' ? '居中' : '右对齐'}
                    >
                      {a === 'left' ? '左' : a === 'center' ? '中' : '右'}
                    </button>
                  ))}
                </div>
                <div className="dr-note-align">
                  <span className="dr-note-align-label">截断</span>
                  {[
                    { v: 'ellipsis', label: '…', title: '显示省略号' },
                    { v: 'shrink', label: '缩', title: '缩小字号显示全文' },
                  ].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      className={`dr-align-btn ${truncateEdit === o.v ? 'on' : ''}`}
                      onClick={() => { setTruncateEdit(o.v); noteMirrorRef.current.truncate = o.v; if (noteVal) saveNote(noteVal, alignEdit, o.v); }}
                      title={o.title}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  className="dr-note-input"
                  value={noteEdit}
                  onChange={e => { setNoteEdit(e.target.value); noteMirrorRef.current.note = e.target.value; }}
                  onKeyDown={e => { if (e.key === 'Enter') saveNote(noteEdit, alignEdit, truncateEdit); }}
                  placeholder="例：《东极岛》"
                />
                <div className="dr-note-actions">
                  <button className={`btn primary ${noteDirty ? 'dr-note-save-dirty' : ''}`} onClick={() => saveNote(noteEdit, alignEdit, truncateEdit)}>保存</button>
                  {noteVal && <button className="btn danger" onClick={() => { setNoteEdit(''); noteMirrorRef.current.note = ''; saveNote('', alignEdit, truncateEdit); }}>清除</button>}
                  {noteDirty
                    ? <span className="dr-note-status unsaved" title="备注有改动还没保存，点「保存」或按 Enter">● 未保存</span>
                    : noteVal ? <span className="dr-note-status saved">✓ 已保存</span> : null}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showQuickApplyEditor && (
        <QuickApplyShuttleModal
          current={quickApplyIds || []}
          defaultIds={defaultQuickApplyIds || []}
          customCats={customCats || []}
          catOrder={catOrder || {}}
          onSave={(ids) => { onQuickApplyChange && onQuickApplyChange(ids); setShowQuickApplyEditor(false); }}
          onClose={() => setShowQuickApplyEditor(false)}
        />
      )}
      {trackPicker && (
        <div className="track-picker-backdrop" onClick={() => setTrackPicker(null)}>
          <div className="track-picker" onClick={(e) => e.stopPropagation()}>
            <div className="track-picker-title">移到哪条轴</div>
            <div className="track-picker-list">
              {TRACK_LABELS.map((label, t) => {
                const isCur = t === trackPicker.t;
                return (
                  <button
                    key={t}
                    type="button"
                    className={`track-picker-item ${isCur ? 'on' : ''}`}
                    style={{ '--tp-accent': TRACK_FAMILY_COLOR[t] }}
                    disabled={isCur}
                    onClick={() => {
                      onMoveTrack && onMoveTrack(trackPicker.t, trackPicker.arrs, t, trackPicker.cat);
                      setTrackPicker(null);
                    }}
                  >
                    <span className="tp-dot" />
                    <span className="tp-name">{label}轴</span>
                    {isCur && <span className="tp-cur">当前</span>}
                  </button>
                );
              })}
            </div>
            <button type="button" className="btn track-picker-cancel" onClick={() => setTrackPicker(null)}>取消</button>
          </div>
        </div>
      )}
    </div>
  );
}

// 穿梭框：自定义快速应用面板里要显示哪些分类（按右侧顺序）
function QuickApplyShuttleModal({ current, defaultIds, customCats, catOrder, onSave, onClose }) {
  const [right, setRight] = React.useState(current);
  const [selL, setSelL] = React.useState(new Set()); // 左侧选中（id）
  const [selR, setSelR] = React.useState(new Set()); // 右侧选中
  const [qL, setQL] = React.useState(''); // 左列搜索词
  const [qR, setQR] = React.useState(''); // 右列搜索词
  const [dragId, setDragId] = React.useState(null);
  const [dragOverIdx, setDragOverIdx] = React.useState(null);

  // 按分类目录树 DFS 顺序展平所有 id（跟侧栏树的渲染顺序一致，含 catOrder 拖拽顺序）
  const allIds = React.useMemo(() => {
    // 构建 parentId -> [childIds] 映射（含内置 CAT_TREE.children + customCats parentId）
    const childMap = {};
    CAT_TREE.forEach(n => { if (n.children) childMap[n.id] = [...n.children]; });
    (customCats || []).forEach(c => {
      if (!childMap[c.parentId]) childMap[c.parentId] = [];
      childMap[c.parentId].push(c.id);
    });
    // 应用 catOrder 排序（用户拖拽顺序）
    if (catOrder) {
      Object.keys(childMap).forEach(pid => {
        if (catOrder[pid]) {
          const ord = catOrder[pid];
          childMap[pid].sort((a, b) =>
            (ord.indexOf(a) === -1 ? 999 : ord.indexOf(a)) -
            (ord.indexOf(b) === -1 ? 999 : ord.indexOf(b))
          );
        }
      });
    }
    // 顶层顺序
    const builtTop = CAT_TREE.map(n => n.id);
    let topOrder;
    if (catOrder && catOrder.root) {
      topOrder = catOrder.root.filter(id => builtTop.includes(id));
      builtTop.forEach(id => { if (!topOrder.includes(id)) topOrder.push(id); });
    } else {
      topOrder = builtTop;
    }
    // DFS 展平：父在前，子紧跟其后
    const out = [];
    function walk(id) {
      out.push(id);
      (childMap[id] || []).forEach(walk);
    }
    topOrder.forEach(walk);
    return out;
  }, [customCats, catOrder]);

  const left = allIds.filter(id => !right.includes(id));

  const lookup = (id) => CAT_BY_ID[id];
  // 搜索过滤：按分类名匹配（空词＝全部）
  function matchQ(id, q) {
    if (!q.trim()) return true;
    const c = lookup(id);
    const name = (c && (c.label || c.name)) || '';
    return name.toLowerCase().includes(q.trim().toLowerCase());
  }

  function toggleSel(side, id) {
    if (side === 'L') {
      setSelL(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    } else {
      setSelR(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }
  }
  function moveToRight() {
    if (selL.size === 0) return;
    const toMove = left.filter(id => selL.has(id));
    setRight(r => [...r, ...toMove]);
    setSelL(new Set());
  }
  function moveToLeft() {
    if (selR.size === 0) return;
    setRight(r => r.filter(id => !selR.has(id)));
    setSelR(new Set());
  }
  function dblClick(side, id) {
    if (side === 'L') setRight(r => [...r, id]);
    else setRight(r => r.filter(x => x !== id));
  }
  function onDragStart(id) { setDragId(id); }
  function onDragOver(e, idx) { e.preventDefault(); setDragOverIdx(idx); }
  function onDrop(e, idx) {
    e.preventDefault();
    if (!dragId) return;
    setRight(r => {
      const without = r.filter(x => x !== dragId);
      const insertAt = without.findIndex(x => r.indexOf(x) >= idx);
      const final = insertAt < 0 ? [...without, dragId] : [...without.slice(0, insertAt), dragId, ...without.slice(insertAt)];
      return final;
    });
    setDragId(null);
    setDragOverIdx(null);
  }
  function renderItem(id, side, idx) {
    const c = lookup(id);
    if (!c) return null;
    const selected = side === 'L' ? selL.has(id) : selR.has(id);
    // 有 parentId = 自定义（可删）；否则 = 系统（不可删）
    const isCustom = !!c.parentId;
    return (
      <div
        key={id}
        className={`qa-item ${selected ? 'on' : ''} ${dragOverIdx === idx && side === 'R' ? 'drop-target' : ''}`}
        onClick={() => toggleSel(side, id)}
        onDoubleClick={() => dblClick(side, id)}
        draggable={side === 'R'}
        onDragStart={() => side === 'R' && onDragStart(id)}
        onDragOver={(e) => side === 'R' && onDragOver(e, idx)}
        onDrop={(e) => side === 'R' && onDrop(e, idx)}
        onDragEnd={() => { setDragId(null); setDragOverIdx(null); }}
      >
        <span className="qa-dot" style={{ background: c.color }} />
        <span className="qa-label">{c.label || c.name}</span>
        {isCustom && <span className="qa-tag qa-tag-custom">自定义</span>}
        {side === 'R' && (() => {
          const displayKey = c.key || (idx === 7 ? '8' : idx === 8 ? '9' : null);
          return displayKey ? <span className="qa-key">{displayKey}</span> : null;
        })()}
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal qa-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">自定义快速应用分类</div>
        <div className="modal-hint">把右边那些分类带到主面板上。单击选中（可多选），→/← 移动；双击直接移到对面；右侧可拖拽改顺序。前 9 个会自动绑 1-9 数字键。</div>
        <div className="qa-shuttle">
          <div className="qa-col">
            <div className="qa-col-head">可选 <b>{selL.size}</b> / {left.length}</div>
            <div className="qa-search">
              <svg className="qa-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M11 11l3.2 3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              <input type="text" className="qa-search-input" placeholder="搜索分类名" value={qL} onChange={(e) => setQL(e.target.value)} />
            </div>
            <div className="qa-list">
              {left.map((id, idx) => matchQ(id, qL) ? renderItem(id, 'L', idx) : null)}
            </div>
            <div className="qa-col-foot">按分类目录树排序</div>
          </div>
          <div className="qa-arrows">
            <button className="btn" disabled={selL.size === 0} onClick={moveToRight} title="移到已选">→</button>
            <button className="btn" disabled={selR.size === 0} onClick={moveToLeft} title="移回可选">←</button>
          </div>
          <div className="qa-col">
            <div className="qa-col-head">已选 <b>{selR.size}</b> / {right.length}</div>
            <div className="qa-search">
              <svg className="qa-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M11 11l3.2 3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              <input type="text" className="qa-search-input" placeholder="搜索分类名" value={qR} onChange={(e) => setQR(e.target.value)} />
            </div>
            <div className="qa-list">
              {right.map((id, idx) => matchQ(id, qR) ? renderItem(id, 'R', idx) : null)}
            </div>
            <div className="qa-col-foot">按快捷键排序</div>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={() => setRight([...defaultIds])}>恢复默认</button>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" onClick={() => onSave(right)}>保存</button>
        </div>
      </div>
    </div>
  );
}

export { DailyTable, QuickApplyShuttleModal };
